import React, { useState, useCallback } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import Modal from "../components/modals/Modal";
import { IconAlertTriangle } from "@tabler/icons-react";
import DropzoneField from "../components/dropzone/DropzoneField";

const CsatolandoMedia = () => {
  const { order, updateOrder } = useOrder();
  const navigate = useStepNavigate();

  const [formData, setFormData] = useState({
    labfenykep: [],
    ambulans_lap: [],
    nyilatkozat: [],
    eukerd: [],
  });

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    fieldName: null,
    index: null,
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const fieldLabels = {
    labfenykep: "Lábfénykép",
    ambulans_lap: "Ambuláns lap",
    nyilatkozat: "Nyilatkozat",
    eukerd: "EÜ kérdőív",
  };

  const requiredFields =
    order.hmeliorder?.hmeliorder_order_type === 1
      ? ["labfenykep"]
      : order.hmeliorder?.hmeliorder_order_type === 2
      ? ["labfenykep", "nyilatkozat", "eukerd"]
      : order.hmeliorder?.hmeliorder_order_type === 3
      ? ["labfenykep"]
      : [];

  const isFormValid = requiredFields.every(
    (field) => formData[field].length > 0
  );

  const handleDrop = useCallback((fieldName, acceptedFiles) => {
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;

    const processed = acceptedFiles
      .filter((file) => {
        if (!validTypes.includes(file.type)) {
          setErrorModal({
            show: true,
            title: "Nem támogatott fájl",
            message: "Csak JPG, PNG vagy PDF tölthető fel!",
          });
          return false;
        }

        if (file.size > maxSize) {
          setErrorModal({
            show: true,
            title: "Túl nagy fájl",
            message: "A fájl nem lehet nagyobb 5MB-nál!",
          });
          return false;
        }

        return true;
      })
      .map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        originalFile: file,
      }));

    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ...processed],
    }));
  }, []);

  const requestDelete = (fieldName, index) => {
    setDeleteModal({
      show: true,
      fieldName,
      index,
    });
  };

  const confirmDelete = () => {
    const { fieldName, index } = deleteModal;

    setFormData((prev) => {
      URL.revokeObjectURL(prev[fieldName][index]?.preview);
      return {
        ...prev,
        [fieldName]: prev[fieldName].filter((_, i) => i !== index),
      };
    });

    setDeleteModal({
      show: false,
      fieldName: null,
      index: null,
    });
  };

  const cancelDelete = () => {
    setDeleteModal({
      show: false,
      fieldName: null,
      index: null,
    });
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      setErrorModal({
        show: true,
        title: "Hiányzó fájlok",
        message: "Minden kötelező dokumentumot fel kell tölteni!",
      });
      return;
    }

    updateOrder({
      ...order,
      csatolmanyok: formData,
    });

    navigate("/Meretvetel");
  };

  const closeErrorModal = () => {
    setErrorModal({ show: false, title: "", message: "" });
  };

  return (
    <TablerLayout title="Szükséges dokumentumok csatolása">
      <div className="row justify-content-center">
        <div className="col-md-10">

          {requiredFields.length > 0 || order.order_type === 1 ? (
            <>
              {requiredFields.map((fieldName) => (
                <DropzoneField
                  key={fieldName}
                  label={fieldLabels[fieldName]}
                  files={formData[fieldName]}
                  onDropFiles={(files) => handleDrop(fieldName, files)}
                  onRequestDelete={(index) =>
                    requestDelete(fieldName, index)
                  }
                />
              ))}

              {order.hmeliorder?.hmeliorder_order_type === 1 ||
              order.hmeliorder?.hmeliorder_order_type === 3 ? (
                <DropzoneField
                  key="ambulans_lap"
                  label={fieldLabels["ambulans_lap"]}
                  files={formData["ambulans_lap"]}
                  onDropFiles={(files) => handleDrop("ambulans_lap", files)}
                  onRequestDelete={(index) =>
                    requestDelete("ambulans_lap", index)
                  }
                />
              ) : null}
            </>
          ) : (
            <p className="text-center fs-5">Nincs szükséges dokumentum feltöltés.</p>
          )}

          <div className="d-flex justify-content-between mt-4">
            <StepperNavButton
              side="left"
              label="Vissza"
              onClick={() => navigate("/BetetTipus")}
            />
            <StepperNavButton
              side="right"
              label="Tovább"
              onClick={handleSubmit}
              disabled={!isFormValid}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={errorModal.show}
        onClose={closeErrorModal}
        title={errorModal.title}
        type="danger"
        confirmText="Rendben"
        confirmClass="btn btn-primary"
        icon={<IconAlertTriangle size={70} className="text-danger" />}
      >
        <p className="fs-5">{errorModal.message}</p>
      </Modal>

      <Modal
        isOpen={deleteModal.show}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Fájl törlése"
        type="warning"
        confirmText="Igen"
        secondaryText="Mégsem"
        confirmClass="btn btn-danger"
        secondaryClass="btn btn-outline-secondary"
        showSecondary={true}
        showCancel={true}
        icon={<IconAlertTriangle size={70} className="text-warning" />}
      >
        <p className="fs-5">
          Biztosan törölni szeretné ezt a fájlt?
        </p>
      </Modal>
    </TablerLayout>
  );
};

export default CsatolandoMedia;
